// @ts-check

/**
 * Product switcher Web Component.
 * - Intercepts shape/fabric card clicks
 * - Fetches new product HTML via Section Rendering API
 * - Swaps hero unit content without page reload
 * - Updates browser history with pushState
 */
(() => {
  class ProductSwitcher extends HTMLElement {
    connectedCallback() {
      /** @type {string} */
      this.sectionId = this.getAttribute('data-section-id') || '';

      // Intercept clicks on shape/fabric cards
      this.addEventListener('click', (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        const card = target.closest('[data-product-url]');

        if (card && card instanceof HTMLElement) {
          const url = card.getAttribute('data-product-url');
          const isDisabled = card.getAttribute('aria-disabled') === 'true';

          if (url && !isDisabled) {
            e.preventDefault();
            this._switchProduct(url);
          }
        }
      });
    }

    /**
     * Fetch new product HTML and swap content
     * @param {string} productUrl
     */
    async _switchProduct(productUrl) {
      try {
        // Fetch new section HTML via Section Rendering API
        const fetchUrl = `${productUrl}?section_id=${this.sectionId}`;
        const response = await fetch(fetchUrl);

        if (!response.ok) throw new Error('Failed to fetch product');

        const html = await response.text();

        // Parse HTML safely using DOMParser
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Extract the new product-switcher content
        const newContent = doc.querySelector('product-switcher');

        if (newContent) {
          // Clear current content
          while (this.firstChild) {
            this.removeChild(this.firstChild);
          }

          // Append new children
          while (newContent.firstChild) {
            this.appendChild(newContent.firstChild);
          }

          // Update URL with pushState
          window.history.pushState({ path: productUrl }, '', productUrl);

          // Scroll to top of hero
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (error) {
        console.error('Product switch error:', error);
        // Fallback: normal navigation
        window.location.href = productUrl;
      }
    }
  }

  customElements.define('product-switcher', ProductSwitcher);

  // Handle browser back/forward
  window.addEventListener('popstate', () => {
    window.location.reload();
  });
})();
